import {Injectable} from '@angular/core';
import {AngularFirestore} from '@angular/fire/firestore';
import algoliasearch from 'algoliasearch';
import {UserService} from './user.service';
import {User} from '../model/user.model';
import {ProvinceModel} from '../model/province.model';
import {ProvinceService} from './province.service';
import {CountyService} from './county.service';
import {CountyModel} from '../model/county.model';
import {SrsService} from './srs.service';
import {DpsService} from './dps.service';
import {AlgoliaUtil} from '../util/algolia.util';
import {Searchable} from '../pages/children/children.component';

@Injectable({
    providedIn: 'root'
})
export class FilterChildService {

    private user: User;

    organizationsUniqueIndex;
    indexByVisitDate;
    onlyMalnutritionCases = false;

    public searchable: Searchable;

    public set setSearchable(searchable: Searchable) {
        this.searchable = searchable;
    };

    constructor(public firestore: AngularFirestore,
                public userService: UserService,
                private provinceService: ProvinceService,
                private countyService: CountyService,
                private srsService: SrsService,
                private dpsService: DpsService) {
        const client = algoliasearch(AlgoliaUtil.APP_ID, AlgoliaUtil.getSearchApiKey());
        this.organizationsUniqueIndex = client.initIndex(AlgoliaUtil.getOrganizationUniqueIndex());
        this.indexByVisitDate = client.initIndex(AlgoliaUtil.getChildrenIndex());
    }

    // ---- "Ver como" SRS / DPS (só supervisores nacionais — ver isNationalSupervisor
    // no componente). Escopo simulado: filtra a lista de províncias do modal e
    // já marca as províncias correspondentes. O filtro real continua sendo
    // `provincesSelected` (province:<name>), então Municipio/Organización herdam.
    simulatedScopeValue = '';
    private scopedProvinceIds: number[] = null;

    // Memoizados: os getters são chamados a cada change detection e, se
    // recriassem o array (e os objetos) toda vez, o *ngFor dentro do <select>
    // reconstruía as <option> a cada ciclo — o dropdown "piscava" e não deixava
    // selecionar. A chave de cache é o tamanho das listas de origem.
    private srsOptionsCache: {key: string, value: Array<{id: number, name: string, provinceCount: number}>} = null;
    private dpsOptionsCache: {key: string, value: any[]} = null;

    get srsOptions() {
        const srs = this.srsService.listAll();
        const provinces = this.provinceService.listAll();
        const key = srs.length + ':' + provinces.length;
        if (this.srsOptionsCache && this.srsOptionsCache.key === key) {
            return this.srsOptionsCache.value;
        }
        const value = this.dedupeById(srs)
            .map(s => ({
                id: s.id,
                name: s.name,
                provinceCount: provinces.filter(p => p.srs === s.id).length
            }))
            .filter(o => o.provinceCount > 0)
            .sort((a, b) => a.name.localeCompare(b.name));
        this.srsOptionsCache = {key, value};
        return value;
    }

    get dpsOptions() {
        const dps = this.dpsService.listAll();
        const key = String(dps.length);
        if (this.dpsOptionsCache && this.dpsOptionsCache.key === key) {
            return this.dpsOptionsCache.value;
        }
        const value = this.dedupeById(dps).sort((a, b) => a.name.localeCompare(b.name));
        this.dpsOptionsCache = {key, value};
        return value;
    }

    // SrsService/DpsService duplicam entradas (onSnapshot com includeMetadataChanges
    // e sem guarda) — deduplica por id.
    private dedupeById<T extends {id: number}>(list: T[]): T[] {
        const seen = new Set<number>();
        return (list || []).filter(item => {
            if (seen.has(item.id)) {
                return false;
            }
            seen.add(item.id);
            return true;
        });
    }

    applySimulatedScope(value: string) {
        this.simulatedScopeValue = value || '';
        this.provincesSelected = [];
        this.provincesIdsSelected = [];
        this.countysSelected = [];
        this.organizationSelected = [];
        this.scopedProvinceIds = null;

        const all = this.dedupeById(this.provinceService.listAll());
        if (this.simulatedScopeValue.startsWith('srs-')) {
            const srsId = parseInt(this.simulatedScopeValue.slice(4), 10);
            const provs = all.filter(p => p.srs === srsId);
            this.scopedProvinceIds = provs.map(p => p.id);
            provs.forEach(p => {
                this.provincesSelected.push(p.name);
                this.provincesIdsSelected.push(p.id);
            });
        } else if (this.simulatedScopeValue.startsWith('dps-')) {
            const dpsId = parseInt(this.simulatedScopeValue.slice(4), 10);
            const dps = this.dpsService.listAll().find(d => d.id === dpsId);
            const prov = dps ? all.find(p => p.id === dps.province) : null;
            if (prov) {
                this.scopedProvinceIds = [prov.id];
                this.provincesSelected.push(prov.name);
                this.provincesIdsSelected.push(prov.id);
            }
        }

        this.searchProvince();
    }

    private resetSimulatedScope() {
        this.simulatedScopeValue = '';
        this.scopedProvinceIds = null;
    }

    // Checkbox "Solo con casos" de cada modal. Por padrão marcado — o usuário
    // quase nunca quer ver opção vazia, e os contadores já respeitam o filtro
    // de perfil. Ao desmarcar, mostra a lista completa.
    onlyProvincesWithCases = true;
    onlyCountiesWithCases = true;
    onlyOrganizationsWithCases = true;

    get visibleProvinces(): Array<ProvinceModel> {
        let list = this.provinces;
        if (this.scopedProvinceIds) {
            list = list.filter(p => this.scopedProvinceIds.includes(p.id));
        }
        if (!this.onlyProvincesWithCases || this.searchingProvince) {
            return list;
        }
        return list.filter(p =>
            this.provincesSelected.includes(p.name) || (this.provinceCount[p.name] || 0) > 0);
    }

    get visibleCounties(): Array<CountyModel> {
        const list = this.counties || [];
        if (!this.onlyCountiesWithCases || this.searchingCounty) {
            return list;
        }
        return list.filter(c =>
            this.countysSelected.includes(c.name) || (this.countiesCount[c.name] || 0) > 0);
    }

    get visibleOrganizations(): Array<any> {
        if (!this.onlyOrganizationsWithCases || this.searchingOrganization) {
            return this.organizations;
        }
        return this.organizations.filter(o => {
            const key = (o.organizationName || '').trim();
            return this.organizationSelected.includes(o.organizationName) || (this.organizationsCount[key] || 0) > 0;
        });
    }

    // Total de casos da lista visível de cada modal (soma dos contadores). Os
    // contadores já respeitam o filtro de perfil, então isto é "quantos casos
    // você pode ver, agrupados por província / município / organização".
    get provincesCaseTotal(): number {
        return this.visibleProvinces.reduce((sum, p) => sum + (this.provinceCount[p.name] || 0), 0);
    }

    get countiesCaseTotal(): number {
        return this.visibleCounties.reduce((sum, c) => sum + (this.countiesCount[c.name] || 0), 0);
    }

    get organizationsCaseTotal(): number {
        return this.visibleOrganizations.reduce(
            (sum, o) => sum + (this.organizationsCount[(o.organizationName || '').trim()] || 0), 0);
    }

    private provinces = new Array<ProvinceModel>();
    provinceCount = {};
    provinceSearchTerm = '';
    searchingProvince = true;
    provincesSelected = [];
    provincesIdsSelected = [];

    searchProvince() {
        this.searchingProvince = true;
        this.provinceService.listByProfile().then(async prov => {
            this.provinces = this.provinceSearchTerm ? prov.filter(item => item.name.toLowerCase().includes(this.provinceSearchTerm.toLowerCase())) : prov;
            let facetFilters = this.searchable.getFilters();
            await this.indexByVisitDate.search('', {
                facetFilters: facetFilters,
                facets: ['province']
            }).then(data2 => {
                this.provinceCount = (data2.facets && data2.facets.province) || {};
            });
            this.searchingProvince = false;
        });
    }

    clearProvinceSearch() {
        this.provinceSearchTerm = '';
        this.searchProvince();
    }

    toogleProvince(province) {
        if (this.provincesSelected.includes(province.name)) {
            this.provincesSelected = this.provincesSelected.filter(item => item !== province.name);
            this.provincesIdsSelected = this.provincesIdsSelected.filter(item => item !== province.id);
        } else {
            this.provincesSelected.push(province.name);
            this.provincesIdsSelected.push(province.id);
        }
        this.organizationSelected = [];
    }

    cleanProvinceFilters() {
        this.provincesSelected = [];
        this.provincesIdsSelected = [];
        this.countysSelected = [];
        this.resetSimulatedScope();
        this.searchable.search(true);
    }

    provinceFilter() {
        this.countysSelected = [];
        this.searchable.search(true);
    }

    //Countys
    private counties = new Array<CountyModel>();
    countiesCount = {};
    countySearchTerm = '';
    searchingCounty = true;
    countysSelected = [];

    async searchCounty() {
        this.searchingCounty = true;
        if (this.provincesIdsSelected.length > 0) {
            this.counties = this.countyService.listByProvinces(this.provincesIdsSelected);
        } else {
            this.counties = this.countyService.listAll;
        }
        this.counties = (this.counties || []).sort((a, b) => a.name.localeCompare(b.name));
        if (this.countySearchTerm) {
            this.counties = this.counties.filter(item => item.name.toLowerCase().includes(this.countySearchTerm.toLowerCase()));
        }

        let facetFilters = this.searchable.getFilters();
        await this.indexByVisitDate.search('', {
            facetFilters: facetFilters,
            facets: ['county']
        }).then(data2 => {
            this.countiesCount = (data2.facets && data2.facets.county) || {};
        });

        this.searchingCounty = false;
    }

    clearCountySearch() {
        this.countySearchTerm = '';
        this.searchCounty();
    }

    toogleCounty(county) {
        if (this.countysSelected.includes(county.name)) {
            this.countysSelected = this.countysSelected.filter(item => item !== county.name);
        } else {
            this.countysSelected.push(county.name);
        }
        this.organizationSelected = [];
    }

    cleanCountyFilters() {
        this.countysSelected = [];
        this.searchable.search(true);
    }

    //Organizations
    private organizations = [];
    organizationsCount = {};
    organizationSearchTerm = '';
    searchingOrganization = true;
    organizationSelected = [];

    async searchOrganization() {
        this.searchingOrganization = true;
        const provinces = this.provincesSelected.map(p => (`province:${p}`));
        const counties = this.countysSelected.map(c => (`county:${c}`));

        this.organizationsUniqueIndex.search(this.organizationSearchTerm, {
            facetFilters: [provinces, counties],
            hitsPerPage: 1000,
            attributesToRetrieve: ['*']
        }).then(async data => {
            this.searchingOrganization = false;
            this.organizations = data.nbHits > 0 ? data.hits : [];
            this.organizations = this.organizations.filter((v, i, a) => a.findIndex(v2 => (v2.organizationName === v.organizationName)) === i);

            let facetFilters = this.searchable.getFilters();
            await this.indexByVisitDate.search('', {
                facetFilters: facetFilters,
                facets: ['organizationName']
            }).then(data2 => {
                this.organizationsCount = (data2.facets && data2.facets.organizationName) || {};
            });

            // O índice único de organizações é limitado a 1000 (ordem alfabética),
            // então a própria org do usuário pode nem vir na lista. Garante que
            // toda organização com casos no recorte apareça — os contadores já
            // respeitam o filtro de perfil. A lista visível (só com casos ou
            // completa) é resolvida pelo getter `visibleOrganizations`.
            const term = (this.organizationSearchTerm || '').toLowerCase();
            const known = new Set(this.organizations.map(o => (o.organizationName || '').trim()));
            Object.keys(this.organizationsCount).forEach(name => {
                const trimmed = name.trim();
                if (known.has(trimmed) || (term && !trimmed.toLowerCase().includes(term))) {
                    return;
                }
                this.organizations.push({organizationName: name, objectID: trimmed});
            });
        });
    }

    clearOrganizationSearch() {
        this.organizationSearchTerm = '';
        this.searchOrganization();
    }

    toogleOrganization(organization) {
        if (this.organizationSelected.includes(organization['organizationName'])) {
            this.organizationSelected = this.organizationSelected.filter(item => item !== organization['organizationName']);
        } else {
            this.organizationSelected.push(organization['organizationName']);
        }
    }

    cleanOrganizationFilters() {
        this.organizationSelected = [];
        this.searchable.search(true);
    }

    // Filtros do modal. `group` define a seção de exibição no modal, na mesma
    // ordem de My Cases: nível de desnutrición primeiro, alertas depois.
    filterOptions = [
        // Nivel de desnutrición
        {
            id: 5,
            icon: 'user-circle',
            text: 'Desnutrición aguda severa',
            field: 'marker',
            class: 'severe-acute-malnutrition',
            group: 'level'
        },
        {
            id: 6,
            icon: 'user-circle',
            text: 'Desnutrición aguda moderada',
            field: 'marker',
            class: 'moderate-acute-malnutrition',
            group: 'level'
        },
        {
            id: 7,
            icon: 'user-circle',
            text: 'Riesgo de desnutrición aguda',
            field: 'marker',
            class: 'risk-of-acute-malnutrition',
            group: 'level'
        },
        {
            id: 8,
            icon: 'user-circle',
            text: 'Niño fuera de la zona de riesgo',
            field: 'marker',
            class: 'outsite-risk-zone',
            group: 'level'
        },
        {
            id: 4,
            icon: 'healed',
            text: 'Niño recuperado',
            field: 'marker',
            class: 'healed',
            group: 'level'
        },
        // Alertas
        {
            id: 0,
            icon: 'hospital-symbol',
            text: 'Niño referido al hospital',
            field: 'icon.icon',
            class: 'hospital-icon',
            group: 'alert'
        },
        {
            id: 1,
            icon: 'calendar-times',
            text: 'Tratamiento interrumpido',
            field: 'icon.icon',
            class: 'calendar-icon',
            group: 'alert'
        },
        {
            id: 2,
            icon: 'exclamation-triangle',
            text: 'Sin progreso',
            field: 'icon.icon',
            class: 'exclamation-icon',
            group: 'alert'
        },
        {
            id: 3,
            icon: 'share-square',
            text: 'Caso transferido',
            field: 'icon.icon',
            class: 'share-square-icon',
            group: 'alert'
        }
    ];

    get levelFilterOptions() {
        return this.filterOptions.filter(o => o.group === 'level');
    }

    get alertFilterOptions() {
        return this.filterOptions.filter(o => o.group === 'alert');
    }

    filterCounts = {};
    filtersSelected = [];

    async searchFilterCounts() {
        let facetFilters = this.searchable.getFilters();
        await this.indexByVisitDate.search('', {
            facetFilters: facetFilters,
            facets: ['transferredCase', 'alert.class', 'marker']
        }).then(data2 => {
            let transferred = data2.facets['transferredCase'];
            this.filterCounts = {
                ...{'share-square-icon': transferred ? transferred['true'] : 0},
                ...data2.facets['marker'],
                ...data2.facets['alert.class']
            };
        });
    }

    clearFilter() {
        this.filtersSelected = [];
        this.searchable.search(true);
    }

    toogleFilter(filter) {
        if (this.filtersSelected.includes(filter.id)) {
            this.filtersSelected = this.filtersSelected.filter(item => item !== filter.id);
        } else {
            this.filtersSelected.push(filter.id);
        }
    }

}
